import { URLSearchParams } from 'url';

import {
  BreadCrumbElement,
  ComponentLayoutInfo,
  onClickNavigationData,
  TaskMenuItem,
} from '../../Apps/DashboardApp/Models/ComponentLayoutInfo';
import { ConstantFormat, DynamicRegexFormat, StringDataFormat } from '../../Apps/DashboardApp/Models/DataFormat';
import { BreadcrumbElements as BreadcrumbConstants } from '../../Common/Constants/BreadcrumbElements.Constants';
import { NavigateApi } from '../../Components/Models/NavigateApi';
import { isEmptyOrSpaces } from './DataValidity.Helper';

// Flag to check whether observer has been set on breadcrumb or not.
let breadcrumbObserverSet = false;
// Flag to check if shell's breadcrumb feature is available to consume or not.
let isShellAvailableBreadcrumb = false;
// In case the breadcrumb from shell is not available we store the parameters to be used later
// after we are able to guarantee the availability.
let breadcrumbElementsStore: Parameters<typeof setupBreadCrumb> | undefined;

export function isSelectedGroup(navigationGroup: ComponentLayoutInfo[]): ComponentLayoutInfo | undefined {
  const currentUrl = window.location.href;

  let currentGroup: ComponentLayoutInfo | undefined = undefined;

  if (navigationGroup && navigationGroup.length > 0 && !isEmptyOrSpaces(currentUrl)) {
    currentGroup = {} as ComponentLayoutInfo;

    let selectedElementIndex: number = 0;
    for (let i = 0; i < navigationGroup.length; i++) {
      const componetsLayoutInformation = navigationGroup[i];
      if (
        componetsLayoutInformation &&
        componetsLayoutInformation.isSelected &&
        currentUrl.search(componetsLayoutInformation.isSelected) > 0
      ) {
        selectedElementIndex = i;
      }
    }
    currentGroup = navigationGroup[selectedElementIndex];
  }

  return currentGroup;
}

// Selects task menu item from the group.
export function selectedTaskMenuItemFromGroup(
  taskMenuItem: TaskMenuItem[],
  selectedId: string
): TaskMenuItem | undefined {
  if (taskMenuItem && taskMenuItem.length > 0 && !isEmptyOrSpaces(selectedId)) {
    for (let i = 0; i < taskMenuItem.length; i++) {
      if (taskMenuItem[i] && taskMenuItem[i].id === selectedId) {
        return taskMenuItem[i];
      }
    }
  }

  return undefined;
}

// Get information based on the conditions applied.
export function obtainInformationBasedonFormat(info: StringDataFormat, params?: URLSearchParams): string {
  if (info && info.infoData) {
    if (info.infoFormat === 'constant') {
      return (info.infoData as ConstantFormat).value;
    }

    if (info.infoFormat === 'dynamic' || info.infoFormat === 'dynamic-regex') {
      if (params) {
        const infoData = info.infoData as DynamicRegexFormat;
        // It is using the props based on component.
        // This is inorder to keep the logic of getting parameter in 1 place that is in Route layer of React app.
        const propsParameter = params.get(infoData.searchParamsId) ?? '';
        if (info.infoFormat === 'dynamic-regex') {
          if (propsParameter && !isEmptyOrSpaces(propsParameter)) {
            const modifiedText = infoData.value_format.replace(infoData.replaceRegex, propsParameter);
            return modifiedText;
          }
        }

        // Returning value corresponsing to Dynamic data format.
        return propsParameter;
      }
    }
  }

  // If none of the options match we return a blank string back;
  return '';
}

// This is responsible for setting breadcrumb observer at app level.
// It will create a listener and allow any functionality related to breadcrumb happen after the
// breadcrumb feature is available from shell.
export function setupBreadcrumbObserverApp(defaultCallback: () => void, callbackWorkspaceNode: () => void) {
  if (breadcrumbObserverSet) {
    return;
  }
  breadcrumbObserverSet = true;

  const getBreadcrumbNode = () => {
    const shell = document.querySelector(BreadcrumbConstants.ShellQuerySelector)?.shadowRoot;
    const breadcrumb = shell?.querySelector(BreadcrumbConstants.ShellBreadcrumbElement)?.shadowRoot;
    const workspaceBreadcrumb = breadcrumb?.getElementById(BreadcrumbConstants.BreadcrumbWorkspaceNode);

    // If workspace breadcrumb node , is available it is save to assume that breadcrumb is available in shell.
    if (workspaceBreadcrumb) {
      if (!isShellAvailableBreadcrumb) {
        defaultCallback();
      }
      isShellAvailableBreadcrumb = true;
    }

    return workspaceBreadcrumb;
  };

  const eventCallback = () => {
    const breadcrumbNode = getBreadcrumbNode();
    if (breadcrumbNode) {
      breadcrumbNode!.onclick = () => {
        callbackWorkspaceNode();
        return false;
      };

      if (breadcrumbElementsStore) {
        setupBreadCrumb(breadcrumbElementsStore[0], breadcrumbElementsStore[1], breadcrumbElementsStore[2]);
        breadcrumbElementsStore = undefined;
      }
      document.removeEventListener(BreadcrumbConstants.ShellBreadcrumbEventName, eventCallback);
    }
  };

  const breadcrumbNode = getBreadcrumbNode();
  if (breadcrumbNode) {
    eventCallback();
  } else {
    document.addEventListener(BreadcrumbConstants.ShellBreadcrumbEventName, eventCallback);
  }
}

// This adds the requested breadcrumb elements , but will ignore it if the shell is not available.
// The provided parameters will be stored in the app to be used once the shell is ready for use.
export function setupBreadCrumb(
  breadcrumbElements: BreadCrumbElement[],
  navigateApi: NavigateApi,
  params: URLSearchParams
) {
  // If Breadcrumb is not available from shell then there is no point in adding the elements
  // So what we do is add them in the store which can be picked later for use.
  if (!isShellAvailableBreadcrumb) {
    breadcrumbElementsStore = [breadcrumbElements, navigateApi, params];
    return;
  }

  // Getting the parent breadcrumb element provided by the shell.
  const parentShellElement = document.querySelector(`${BreadcrumbConstants.ShellQuerySelector}`);

  if (breadcrumbElements && parentShellElement && params && navigateApi) {
    // We could have simply used innerHtml but the problem with that approach is
    // it does not clear the event which can lead to memory leaks.

    const breadcrumbs = parentShellElement.querySelectorAll(`${BreadcrumbConstants.BreadcrumbParentTagName}`);
    breadcrumbs.forEach(breadcrumb => parentShellElement?.removeChild(breadcrumb));

    if (breadcrumbElements.length > 0) {
      const breadcrumbParent = document.createElement(BreadcrumbConstants.BreadcrumbParentTagName);
      breadcrumbParent.id = BreadcrumbConstants.BreadcrumbParentId;

      breadcrumbElements.forEach((breadcrumbElement: BreadCrumbElement) => {
        if (
          breadcrumbElement &&
          breadcrumbElement.id &&
          breadcrumbElement.label &&
          breadcrumbElement.onClickNavigationData
        ) {
          const labelData = obtainInformationBasedonFormat(breadcrumbElement.label, params);

          const isClickEventDisabled = breadcrumbElement.onClickNavigationData.onNavigationClickBehavior === 'none';

          const createdBreadCrumbElement = createBreadCrumbElement(
            breadcrumbElement.id,
            labelData,
            isClickEventDisabled,
            breadcrumbElement.onClickNavigationData,
            navigateApi,
            params
          );

          if (createdBreadCrumbElement) {
            breadcrumbParent.appendChild(createdBreadCrumbElement);
          }
        }
      });

      parentShellElement.append(breadcrumbParent);
    }
  }
}

// Describes the on click navigation behavior for the element.
export function onClickNavigation(
  onClickNavigationData: onClickNavigationData,
  navigateApi: NavigateApi,
  urlData: string
) {
  if (onClickNavigationData) {
    if (urlData && onClickNavigationData) {
      if (onClickNavigationData.onNavigationClickBehavior === 'spa-navigation' && navigateApi) {
        navigateApi.navigateTo(urlData);
      }

      if (onClickNavigationData.onNavigationClickBehavior === 'href') {
        window.open(urlData, '_blank');
      }
    }
  }
}

/**
 * This function is to get onClick navigation behavior by using the route params.
 * @param onClickNavigation This defines the onClick Navigation Data
 * @param navigateApi Navigation Api to be used for navigating by maintaining Single page application behavior
 * @param params Provides the Routes params to obtain url data.
 */
export function onClickNavigationParams(
  onClickNavigationData: onClickNavigationData,
  navigateApi: NavigateApi,
  params: URLSearchParams
) {
  if (onClickNavigationData && navigateApi && params) {
    const urlData = obtainInformationBasedonFormat(onClickNavigationData, params);
    onClickNavigation(onClickNavigationData, navigateApi, urlData);
  }
}

// Responsible for creating Breadcrumb element which will be attached to parent breadcrumb element provided by shell.
function createBreadCrumbElement(
  id: string,
  label: string,
  disableOnClick: boolean,
  onClickNavigationData: onClickNavigationData,
  navigateApi: NavigateApi,
  params: URLSearchParams
) {
  if (!isEmptyOrSpaces(id) && !isEmptyOrSpaces(label)) {
    const element = document.createElement(BreadcrumbConstants.BreadcrumbElementTagName);
    element.id = id;
    element.textContent = label;

    if (onClickNavigationData && navigateApi) {
      const urlData = obtainInformationBasedonFormat(onClickNavigationData, params);

      const clickEvent = () => {
        if (!disableOnClick) {
          onClickNavigation(onClickNavigationData, navigateApi, urlData);
        }
        // Cancelling Event to prevent Href getting triggered.
        return false;
      };

      element.onclick = clickEvent;
      (element as HTMLAnchorElement).href = urlData;
    }

    return element;
  }
}
